import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TranslationsPanel } from "@/components/welcome/TranslationsPanel";
import { RedeployLabelCard } from "@/components/welcome/RedeployLabelCard";

function DykTranslations() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/welcome-label/label-text/dyk").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-5" data-testid="dyk-translations">
      <div className="text-sm font-semibold text-slate-800">Regional versions of "Did you know?" lines</div>
      <p className="text-xs text-slate-500">Hindi and regional welcome labels show a line only when its translation is approved. Edit a line above and its translations need approval again.</p>
      {d.items.map((it, i) => {
        const done = Object.values(it.translations).filter((x) => x.approved && !x.stale).length;
        return (
          <div key={it.text} className="rounded-lg border border-slate-100" data-testid={`dyk-line-${i}`}>
            <button onClick={() => setOpen(open === i ? null : i)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50" data-testid={`dyk-line-toggle-${i}`}>
              <span className="flex-1 text-slate-800">{it.text}</span><span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">{done}/{Object.keys(d.languages).length} approved</span></button>
            {open === i && <div className="p-3 pt-0"><TranslationsPanel kind="dyk" refId={it.text} translations={it.translations} languages={d.languages} canApprove={d.can_approve} onUpdated={(next) => setD({ ...d, items: d.items.map((x, j) => (j === i ? { ...x, translations: next } : x)) })} /></div>}
          </div>);
      })}
    </div>
  );
}

const err = (e) => e?.response?.data?.detail || e?.message || "Request failed";
const LISTS = [
  ["about_lines", "About SUNTRONIX lines", "Rotates on every label's footer. Use {since} where the founding year should appear."],
  ["did_you_know", "\u201cDid you know?\u201d service lines", "Fills free space on welcome and courier labels, with a WhatsApp QR."],
  ["handling", "Handling & unboxing notes", "Alternates with \u201cDid you know?\u201d on courier labels, box by box."],
];

function LineList({ k, title, hint, value, onChange, disabled }) {
  return (
    <div className="space-y-1.5" data-testid={`content-${k}`}>
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      <p className="text-xs text-slate-500">{hint} One line per row.</p>
      <Textarea rows={Math.max(3, value.length + 1)} value={value.join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} disabled={disabled} data-testid={`content-${k}-input`} />
    </div>
  );
}

export function LabelContent() {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const apply = (data) => { setD(data); setF({ ...data.saved, established_year: data.saved.established_year || "", whatsapp: data.saved.whatsapp || "" }); };
  useEffect(() => { api.get("/welcome-label/label-content").then((r) => apply(r.data)).catch((e) => toast.error(err(e))); }, []);
  if (!f) return <div className="text-sm text-slate-500">Loading label content…</div>;
  const e = d.effective;
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...Object.fromEntries(LISTS.map(([k]) => [k, f[k].filter((x) => x.trim())])), established_year: f.established_year ? Number(f.established_year) : null, whatsapp: f.whatsapp || null };
      const { data } = await api.put("/welcome-label/label-content", body); apply(data); toast.success("Label content saved — applies to the next print");
    } catch (x) { toast.error(err(x)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]" data-testid="label-content">
      <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-5">
        {LISTS.map(([k, t, h]) => <LineList key={k} k={k} title={t} hint={h} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} disabled={!d.can_edit} />)}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-sm font-semibold text-slate-800">Founding year<Input type="number" value={f.established_year} onChange={(x) => setF({ ...f, established_year: x.target.value })} placeholder="e.g. 2008" disabled={!d.can_edit} data-testid="content-year" /></label>
          <label className="space-y-1 text-sm font-semibold text-slate-800">Support WhatsApp<Input value={f.whatsapp} onChange={(x) => setF({ ...f, whatsapp: x.target.value })} placeholder={`Same as phone (${e.phone})`} disabled={!d.can_edit} data-testid="content-whatsapp" /></label>
        </div>
        {d.can_edit ? <div className="flex justify-end"><Button onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="content-save">{busy ? "Saving…" : "Save label content"}</Button></div>
          : <div className="text-xs text-slate-500">Only management can change label content.</div>}
      </div>
      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm" data-testid="content-contact">
        <div className="font-semibold text-slate-800">Contact strip on every label</div>
        <div className="space-y-1 text-slate-700"><div>Call / WhatsApp: <b>{e.whatsapp}</b></div><div>Email: <b>{e.email}</b></div><div>Website: <b>{e.website}</b></div>{e.tagline && <div>Tagline: <b>{e.tagline}</b></div>}</div>
        <p className="text-xs text-slate-500">These come from the Company Profile, so they stay in one place. Change them there.</p>
        <div className="border-t border-slate-200 pt-3 text-xs text-slate-600">Printed on: welcome labels, courier labels, stock unit labels, carton labels and location labels. The smallest 50×25 labels show the website only.</div>
      </div>
    </div>
    <RedeployLabelCard />
    <DykTranslations />
    </div>
  );
}
